/**
 * スクロールアニメーション機能を提供します。
 *
 * このモジュールは、ヘッダーのスクロール時のクラス制御と、
 * 要素が画面内に入ったときのフェードインアニメーション機能を提供します。
 *
 * common.js の `DOMContentLoaded` イベントで呼び出されます。
 */

// ///////////////////////////////
// ヘッダーアニメーション
// ///////////////////////////////

const SCROLL_THRESHOLD_HEADER = 200; // ヘッダーにクラスを付与するスクロール量 (px)
const HEADER_SCROLL_CLASS = 'navbar-scroll';
let ticking = false; // requestAnimationFrame のスロットリング用

export function initScrollAnimationHeader() {
  const navbar = document.querySelector('.site-header');
  if (!navbar) {
    return;
  }
  const updateHeaderClass = () => {
    const currentScrollY = window.scrollY;
    if (currentScrollY > SCROLL_THRESHOLD_HEADER) {
      navbar.classList.add(HEADER_SCROLL_CLASS);
    } else {
      navbar.classList.remove(HEADER_SCROLL_CLASS);
    }
    const adminBar = document.getElementById('wpadminbar');
    const adminOffset = adminBar ? Math.max(0, adminBar.getBoundingClientRect().bottom) : 0;
    document.documentElement.style.setProperty('--admin-bar-offset', `${adminOffset}px`);
    ticking = false;
  };
  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(updateHeaderClass);
      ticking = true;
    }
  });
  window.addEventListener('resize', updateHeaderClass);
  updateHeaderClass();
}

// ///////////////////////////////
// コンテンツのスクロールフェードインアニメーション
// ///////////////////////////////

// アニメーション対象要素のセレクタと付与するクラス
const TARGET_ELEMENT_SELECTOR = 'body.page .entry-content section';
const ANIMATE_CLASS_TO_ADD = 'js-scroll-animate';
const ANIMATE_ACTIVE_CLASS = 'is-animated';

/**
 * Intersection Observer のオプション。
 * - `rootMargin`: 要素がビューポートに入ったと判定するマージン。
 * - `threshold`: 要素の何%が見えたら発火するか。
 */
const observerOptions = {
  root: null, // ビューポートをルートとする
  rootMargin: '0px 0px -15% 0px', // ビューポートの下から15%手前で発火
  threshold: 0, // 要素が少しでも見えたら発火
};

/** Initialize visibility only; callers do not wait for transition completion. */
export function initScrollAnimationContents() {
  document.querySelectorAll(TARGET_ELEMENT_SELECTOR).forEach((element) => {
    // Animate the outer section once, including all its headings and content.
    const parentSection = element.parentElement.closest('section');
    const content = element.closest('.entry-content');
    if (parentSection && content.contains(parentSection)) return;
    element.classList.add(ANIMATE_CLASS_TO_ADD);
  });
  const animateElements = document.querySelectorAll(`.${ANIMATE_CLASS_TO_ADD}`);
  if (
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ||
    !('IntersectionObserver' in window)
  ) {
    animateElements.forEach((element) => element.classList.add(ANIMATE_ACTIVE_CLASS));
    return;
  }
  if (!animateElements.length) return;
  const observer = new IntersectionObserver((entries, instance) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add(ANIMATE_ACTIVE_CLASS);
      instance.unobserve(entry.target);
    });
  }, observerOptions);
  animateElements.forEach((element) => {
    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      element.classList.add(ANIMATE_ACTIVE_CLASS);
    } else {
      observer.observe(element);
    }
  });
}
